import classNames from 'classnames'
import type { RaceEvent } from 'src/types/domain/RaceEvent'
import { useSoundEffect } from 'src/hooks/useSoundEffect'
import { useClientSideTranslation } from 'src/services/i18n'
import { useGetCurrentLanguage } from 'src/store/game'
import type { MarbleGame } from 'src/types/domain/Game'
import { getTranslatedGameTitle } from 'src/services/i18n/utils/gameTranslations'
import { GameLogo } from '../GameLogo'
import { GameCardVisual } from './components/GameCardVisual'
import { GameCardTags } from './components/GameCardTags'
import { GameCardTimer } from './components/GameCardTimer'

type Props = Readonly<{
  game: MarbleGame
  latestEvent: RaceEvent
  isLoading: boolean
  isHovered: boolean
  isCurrentGame?: boolean
  onClick: () => void
  onMouseEnter: () => void
  onMouseLeave: () => void
}>

export function GameCard({
  game,
  isCurrentGame,
  latestEvent,
  onClick,
  onMouseEnter,
  onMouseLeave,
  isLoading,
  isHovered
}: Props) {
  const { t } = useClientSideTranslation(useGetCurrentLanguage())
  const { playEffect } = useSoundEffect()

  const isScheduled = latestEvent.eventType === 'race_scheduled'

  const handleClick = () => {
    if (!isLoading) {
      playEffect('click')
      onClick()
    }
  }

  const cardClasses = classNames('game-lobby-races_entry', latestEvent.eventType, {
    scheduled: isScheduled,
    loading: isLoading,
    'current-game': isCurrentGame,
    hovered: isHovered
  })

  return (
    <div
      aria-label={`View details for ${game.track.name}`}
      className={cardClasses}
      key={game.id}
      onClick={handleClick}
      onKeyDown={(event) => {
        if (!isScheduled && !isLoading && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault()
          handleClick()
        }
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      role={isScheduled ? 'presentation' : 'button'}
      tabIndex={isScheduled ? -1 : 0}
    >
      <div className="entry-content_inner">
        <GameLogo gameType={game.track.gameType} />
        <GameCardVisual
          isLoading={isLoading}
          trackImage={game.track.image}
          trackName={game.track.name}
          videoPreviewMp4={game.track.videoPreviewMp4}
        />

        <div className="entry-bottom">
          {Boolean(isCurrentGame) && (
            <div className="current-game-indicator">
              <span>{t('Current Track')}</span>
            </div>
          )}

          {Boolean(isScheduled) && (
            <div className="scheduled-game-indicator">
              <GameCardTimer race={game.races[0]} />
            </div>
          )}

          <div className="track-name">{getTranslatedGameTitle(game.track, t)}</div>

          <GameCardTags />
        </div>
      </div>
    </div>
  )
}
